<script setup>
import { computed, ref, onMounted, onUnmounted, watch, getCurrentInstance, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { showModal } from '@/Utils/modalBus'
import _ from 'lodash';
import moment from "moment";
import Spinner from "@/components/ui/Spinner.vue";
import StreamService from '@/services/StreamService';
import PaywallService from '@/services/PaywallService';
import TabGroup from "../../components/Atoms/TabGroup.vue";
import TabItem from "../../components/Atoms/TabItem.vue";
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import utils from '@/utils';
import Title from '../../components/ui/Title.vue';
import VODNav from '@/components/VOD/VODNav.vue';
import Badge from '../../components/Badge.vue';
import Alert from '../../components/Atoms/Alert.vue';
import MetricsService from '../../services/MetricsService';
import UserService from '../../services/UserService';
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus';
import { useRecentFolders } from '@/Utils/useRecentFolders';
import { track, EVENTS } from '@/lib/tracking';

const { recordVisit } = useRecentFolders()
const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const isVideoListLoading = ref(false)
const scopeAlive = ref(true)
const filesLoading = ref(false)
const folderResolving = ref(false)
const trialSubscription = ref(null)
const processingMessage = ref(null)
const stream = ref(null)
const isVodPaid = ref(false)
const emptyVodSub = ref(true)
const vodTrialExpiredIn = ref(0) // day
const aioStorageLimit = ref(null)
const dividerPosition = ref(25)

const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
// Computed from the subscriptions below by getVodSubExpiredDays, the way
// VODBucket does it. It used to read `User/hasVodSubscription.trial` while
// that function assigned to it — writes a computed silently drops — so a paid
// account whose meta still carried a trial kept being told its trial was
// ending.
const isVodTrial = ref(false)
const maxVodFolders = computed(() => store.getters['User/maxVodFolders'])
const maxVodStorage = computed(() => store.getters['User/maxVodStorage'])
const activeTeam = computed(() => store.getters['User/activeTeam'])

/**
 * A trial notice belongs to accounts that are actually on a trial. A paying
 * account can still carry a zero-charge VOD entry — the hosting included in
 * its plan — and every flag below reads "no charge" as "trial", which is how
 * a subscriber on a higher tier ended up being told their trial was ending.
 */
const hasPaidPlan = computed(() => (store.getters['User/subscriptions'] || [])
	.some((sub) => sub?.enabled && Number(sub?.package?.baseCharge) > 0))

const userSubscription = computed(() => {
	return {
		subscription: baseSubscription.value,
		addonSubscriptions: addonSubscriptions.value,
		maxVodItems: maxVodFolders.value,
		maxVodStorage: maxVodStorage.value
	}
})

const calculateAioStorageLimit = async () => {
  const subscriptions = store.state.User.subscriptions;
  const activeAIOSub = _.find(subscriptions, (sub) => sub.category === 'live' && sub.enabled === true)
  if (activeAIOSub) {
    const res = await MetricsService.getSubscriptionStorage(UserService.getUserId(activeTeam.value), activeAIOSub.package._id)
    aioStorageLimit.value = res ? res : null;
  }
}

const folderId = computed(() => route.params?.streamId)
const vodFolder = computed(() => vodStreams.value?.find((vod) => vod._id === folderId.value))
const folderReady = computed(() => !!vodFolder.value?._id && vodFolder.value._id === folderId.value)
const showFolderShellSpinner = computed(() => folderResolving.value && !folderReady.value)
const vodFilesFolderId = computed(() => store.getters['Streams/vodFilesFolderId'])
const playlistSynced = computed(() => vodFilesFolderId.value === folderId.value)
const filesLoadingForView = computed(() =>
	filesLoading.value || (folderReady.value && !playlistSynced.value),
)

const deleteMessage = computed(() => vodFolder.value?.liveParent
	? 'Deleting this folder will permanently remove all videos within it and remove them from the VOD section of your streams. Are you sure you want to proceed?'
	: 'Deleting this folder will permanently remove all files within it. Are you sure you want to continue?'
)

const handleDragging = (e) => {
	e.preventDefault();
	const position = e.pageX
	const wrapper = document.getElementById('resizible-wrapper')
	const percentage = ((position - 260) / wrapper.offsetWidth) * 100
	if (percentage >= 20 && percentage <= 30) {
		dividerPosition.value = percentage.toFixed(2)
	}
	if (position - 260 <= 0) {
		document.removeEventListener('mousemove', handleDragging)
	}
}

const startDragging = () => {
	document.addEventListener('mousemove', handleDragging)
}

const endDragging = () => {
	document.removeEventListener('mousemove', handleDragging)
}

const loadFolderPlaylist = async (streamId) => {
	if (!streamId || !scopeAlive.value) return
	filesLoading.value = true
	store.dispatch('Streams/prepareVodFolder', streamId)
	try {
		await store.dispatch('Streams/getFilesInVODFolder', { streamId })
	} catch (error) {
		if (!scopeAlive.value || route.params?.streamId !== streamId) return
		notify({ group: 'error', text: error?.message || 'Failed to load VOD folder data' })
		await router.push({ name: 'VodBucket' })
	} finally {
		if (route.params?.streamId === streamId) filesLoading.value = false
	}
}

const runFolderBootstrap = async (streamId) => {
	if (!streamId) return
	recordVisit(streamId)
	store.dispatch('Ui/getAllTags')

	const hasCachedFolder = vodStreams.value?.some((vod) => vod._id === streamId)
	if (!hasCachedFolder) {
		folderResolving.value = true
		try {
			await store.dispatch('Streams/getStreams', true)
		} finally {
			folderResolving.value = false
		}
	} else {
		store.dispatch('Streams/getStreams')
	}

	if (!scopeAlive.value || route.params?.streamId !== streamId) return

	if (!vodStreams.value?.some((vod) => vod._id === streamId)) {
		notify({ group: 'error', text: 'Folder not found' })
		await router.push({ name: 'VodBucket' })
		return
	}

	store.dispatch('User/getactiveTeam', null, { root: true })
	getVodSubExpiredDays()
	calculateAioStorageLimit()

	if (vodFilesFolderId.value !== streamId) {
		loadFolderPlaylist(streamId)
	}

	await nextTick()
	handleCloudImportIntent()
}

onMounted(() => {
	if (folderId.value) runFolderBootstrap(folderId.value)
})

watch(
	() => route.params?.streamId,
	(streamId, prevId) => {
		if (!streamId || streamId === prevId) return
		runFolderBootstrap(streamId)
	},
)

onUnmounted(() => {
	scopeAlive.value = false
})

const handleCloudImportIntent = () => {
	if (route.query.new !== 'cloud') return;
	if (vodFolder.value?.liveParent) return;
	const query = { ...route.query };
	delete query.new;
	router.replace({ query }).catch(() => {});
	showModal('cloud-import-box-drop');
}

// Keep subscription storage usage (aioStorageLimit) in sync whenever VOD streams change
watch(vodStreams, async () => {
	await calculateAioStorageLimit();
})

// Subscription data (base/addon subs, quotas) loads asynchronously and often
// after onMounted's first getVodSubExpiredDays() call, so recompute the trial
// flags whenever it changes rather than leaving the first, empty answer up.
watch(userSubscription, () => getVodSubExpiredDays(), { deep: true })

const onStreamDeleteConfirm = async () => {
	// Capture before deletion — refreshing the store clears vodFolder.
	const isLiveToVod = !!vodFolder.value?.liveParent;
	const videoCount = vodFolder.value?.totalFiles;
	store.dispatch('Ui/setLoadingStatus', true);
	// this.processing = true;
	processingMessage.value = 'Deleting stream';

	let streamId = vodFolder.value.liveParent ? vodFolder.value.liveParent : vodFolder.value._id;

	// Try to delete its dvr
	let vodEpisodes
	let permanentDvrArchives
	try {
		vodEpisodes = await StreamService.getStreamDvrArchives(streamId);
	} catch (e) {
		if (!vodFolder.value.liveParent) {
			notify({
				group: 'error',
				text: 'could not retrieve saved vod(s)'
			});
		}
	}

	vodEpisodes = _.map(vodEpisodes, vodEpisode => {
		const ep = _.assign({}, vodEpisode, {
			startTimeDate: new Date(vodEpisode.startTime),
			endTimeDate: new Date(vodEpisode.endTime)
		});

		ep.fileName = `${utils.ago(ep.startTimeDate)}`;
		ep.fileName = ep.fileName.replace('a few seconds ago', 'a seconds ago');
		ep.creationTime = ep.startTimeDate;

		ep.live = false;
		ep.expanded = false;
		ep.streamType = vodFolder.value.type;
		ep.blocking = false;
		ep.size = ep.size ? utils.bytes(ep.size, false, 2) : '--';

		ep.exportProcessing = false;
		ep.exportStatusTimer = null;

		return ep;
	});

	permanentDvrArchives = _.filter(vodEpisodes, { exportStatus: 'EXPORTED' }) || []
	permanentDvrArchives.forEach(async (video) => {
		const q = {
			streamId: streamId,
			from: video.from,
			duration: video.duration,
			abr: video.abr,
			namedHls: video.namedHls
		};
		if (vodFolder.value.liveParent || !video.pseudoVod) {
			await StreamService.deleteStreamDvrArchive(q);
		}
	});

	// try deleting stream
	try {
		await StreamService.deleteStream(vodFolder.value._id);
		// Force refresh to remove deleted VOD folder from store
		await store.dispatch('Streams/getStreams', true);

		track(EVENTS.VOD_FOLDER_DELETED, {
			isLiveToVod,
			videoCount,
		});

	} catch (err) {
		// redirect to stream list
		notify({
			group: 'error',
			title: err.error,
			text: err.message
		});
		await store.dispatch('Streams/getStreams', true);
	}
	router.push({ name: 'VodBucket' });
	store.dispatch('Ui/setLoadingStatus', false);
	// this.processing = false;
}
const getVodSubExpiredDays = () => {
	// Reset before recomputing: this runs again whenever the subscription data
	// loads/changes, so stale flags from an earlier (empty) run must be cleared.
	isVodTrial.value = false;
	isVodPaid.value = false;
	emptyVodSub.value = true;
	vodTrialExpiredIn.value = 0;
	let originalVodSub;
	let paidSub;
	let trialSub;
	let hasMaxVodItems = false;
	if (_.isArray(userSubscription.value)) {
		userSubscription.value.forEach((sub) => {
			if (sub.category === "vod") emptyVodSub.value = false;
			if (sub.category === "vod" && sub.package.baseCharge === 0) {
				trialSub = sub;
				isVodTrial.value = true;
			}
			if (
				sub.category === "vod" &&
				sub.package.baseCharge > 0 &&
				sub.enabled
			) {
				originalVodSub = sub;
			}
			if (
				sub.category !== "vod" &&
				sub.package.definition.maxVodItems &&
				parseInt(sub.package.definition.maxVodItems, 10) > 0
			) {
				paidSub = sub;
				if (sub.enabled === true) hasMaxVodItems = true;
			}
		});
	}
	if (_.isObject(userSubscription.value)) {
		userSubscription.value.addonSubscriptions.forEach((sub) => {
			if (sub.category === "vod") emptyVodSub.value = false;
			if (sub.category === "vod" && sub.package.baseCharge === 0) {
				trialSub = sub;
				isVodTrial.value = true;
			}
			if (
				sub.category === "vod" &&
				sub.package.baseCharge > 0 &&
				sub.enabled
			) {
				originalVodSub = sub;
			}
			if (
				sub.category !== "vod" &&
				sub.package.definition.maxVodItems &&
				parseInt(sub.package.definition.maxVodItems, 10) > 0
			) {
				paidSub = sub;
				if (sub.enabled === true) hasMaxVodItems = true;
			}
		});
	}
	if (trialSub) {
		vodTrialExpiredIn.value = moment(trialSub.cend).diff(moment(), "day");
	}

	if (paidSub) {
		isVodPaid.value = true;
		let paidSubExpiredIn = moment(paidSub.cend).diff(moment(), "day");

		// Compare with the previous expire day to get which is available
		if (trialSub && vodTrialExpiredIn.value > paidSubExpiredIn) {
			paidSubExpiredIn = vodTrialExpiredIn.value;
			isVodPaid.value = false;
		}

		vodTrialExpiredIn.value = paidSubExpiredIn;

		if (hasMaxVodItems) {
			isVodTrial.value = false;
			vodTrialExpiredIn.value = 1;
		}
	}

	if (originalVodSub) {
		isVodPaid.value = true;
		const originalVodSubExpiredIn = moment(originalVodSub.cend).diff(
			moment(),
			"day"
		);

		if (originalVodSubExpiredIn > vodTrialExpiredIn.value) {
			vodTrialExpiredIn.value = originalVodSubExpiredIn;
			isVodTrial.value = false;
		}
	}
	return vodTrialExpiredIn.value;
}

const onStreamNameChange =  async (newName, vod) => {
	if (newName === vodFolder.value.name) return;
	// try changing stream name
	try {
		await StreamService.setStreamName(vod._id, newName);
		PaywallService.updateStreamPaywall(vod._id, { title: newName }).catch(err => {
			console.error('update paywall stream name failed: ', err)
		});
		store.dispatch('Streams/getStreams')
		notify({
			group: 'success',
			title: `The ${newName} name was successfully changed`,
		});
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't change stream name",
			text: err.message
		});
	}
}

const fetchStreamVideoFileList = async () => {
	isVideoListLoading.value = true
	try {
		// reflects newly uploaded files without requiring a manual page refresh.
		await store.dispatch('Streams/getStreams', true)
		await store.dispatch('Streams/getFilesInVODFolder', { streamId: folderId.value })
		emitAppEvent(APP_EVENTS.VIDEO_UPLOADED)
	} catch (e) {
		console.error(e)
		notify({
			group: 'error',
			text: e?.message || 'Failed to refresh video list',
		})
	} finally {
		isVideoListLoading.value = false
	}
}

const onVideoFileAdded = async () => {
	await fetchStreamVideoFileList()
}

// vodFolder is derived from the store, so child-reported changes are applied
// by refetching rather than patching a local copy (unlike StreamsManage)
const onStreamUpdates = async () => {
	await store.dispatch('Streams/getStreams', true)
}

const fetchStreamDetails = async (cb) => {
	await store.dispatch('Streams/getStreams', true)
	if (cb && cb.constructor === Function) {
		cb()
	}
}

// Children read vodFiles from Vuex and emit UI-state patches (expanded, removing)
const onVideoFileUpdate = (videoId, updates) => {
	store.dispatch('Streams/patchVodFile', { videoId, updates })
}

const onVideoFileRemoved = (videoId) => {
	store.dispatch('Streams/removeVodFile', videoId)
	// refresh folder totals (file count / storage) shown in the nav
	store.dispatch('Streams/getStreams', true)
}

const shouldShowSettingsNav = computed(() => route.name === 'VODBucketPlayerSettings' || route.name === 'VODBucketSecuritySettings' ||  route.name === 'StreamsManageVodAdvertising' || route.name === 'VODstreamManagePaywall')

// The folder-opened video grid and the single-video details view use a
// full-width, breadcrumb-driven layout (no VOD sidebar) per the new design.
const isFullWidth = computed(() => route.name === 'VODBucketVideos' || route.name === 'VODStreamManage')
const contentStyle = computed(() => isFullWidth.value
	? {}
	: { '--vod-content-width': `${100 - dividerPosition.value}%` })
</script>

<template>
	<div class="w-full !ml-0" @mouseup="endDragging()">
		<main id="resizible-wrapper" class="flex !flex-wrap lg:!flex-nowrap relative">
			<VODNav v-if="!isFullWidth" :is-vod-trial="isVodTrial" :stream="stream" :aioStorageLimit="aioStorageLimit"
				:style="{ '--vod-nav-width': `${dividerPosition}%` }" @video-added="onVideoFileAdded"
				:vod-sub-expired-days="vodTrialExpiredIn" class="vod-nav-panel pt-2 md:pt-12"
				@route-change="goToItem" />
			<div v-if="!isFullWidth" class="aside-divider !hidden lg:!block" :style="{
				left: `${dividerPosition}%`
			}" @mousedown="startDragging()">
			</div>
			<Spinner v-if="showFolderShellSpinner" text="Loading your videos…"
				:classes="isFullWidth ? 'text-surface-8 w-full min-h-[70vh]' : 'text-surface-8 mt-16 mx-auto'"
				spinner-color="var(--c-surface-8)" spinner-size="15px" />
			<div v-show="folderReady" class="vod-content-panel !pl-4 !pr-4 md:!pl-6 pt-6"
				:class="[
					{ 'vod-content-panel--full': isFullWidth },
					isFullWidth ? 'md:pt-10 md:!pr-6 md:max-w-screen-2xl md:mx-auto' : 'md:pt-24 lg:pt-10'
				]"
				:style="contentStyle">
				<header v-if="route.name !== 'VODStreamManage' && route.name !== 'VODBucketVideos'" class="flex mb-6 items-center flex-wrap gap-y-2">
					<Badge v-if="vodFolder?.liveParent" title="Live-to-VOD" class="!mr-2" />
					<Title :editable="true" @changed="value => onStreamNameChange(value, vodFolder)" :title="vodFolder?.name" />
					<Alert class="md:ml-auto">
						Need more storage/bandwidth?&nbsp;<a class="underline" href="/app/subscribe">Upgrade here</a>!
					</Alert>
				</header>
				<div v-if="isVodTrial && !isVodPaid && !hasPaidPlan">
					<p class="color-danger text-center md:text-left" v-if="vodTrialExpiredIn >= 0">
						Your trial plan will end in {{ vodTrialExpiredIn }} days. Please
						<router-link to="/subscribe">
							Upgrade
						</router-link>
						to extend your service.
					</p>
					<p class="color-danger" v-else>
						Your trial plan has ended. Please
						<router-link to="/subscribe">
							Upgrade
						</router-link>
						to extend your service.
					</p>
				</div>
				<div v-if="(!emptyVodSub || !isVodTrial) && isVodPaid && vodTrialExpiredIn < 0">
					<p class="color-danger">
						Your plan has ended. Please
						<router-link to="/subscribe">
							Upgrade
						</router-link>

						to continue.
					</p>

					<div v-if="isVodTrial && !isVodPaid">
						<p class="color-danger" v-if="vodTrialExpiredIn >= 0">
							Your trial plan will end in {{ vodTrialExpiredIn }} days. Please
							<router-link to="/subscribe?category=live">
								Upgrade
							</router-link>
							to extend your service.
						</p>
						<p class="color-danger" v-else>
							Your trial plan has ended. Please
							<router-link to="/subscribe?category=live">
								Upgrade
							</router-link>
							to extend your service.
						</p>
					</div>
				</div>
				<TabGroup v-if="shouldShowSettingsNav" class="mb-6" type="lines">
        <TabItem
          type="line"
          label="Settings"
					:to="'/vods/' + vodFolder?._id + '/settings/player-interface'"
          :isActive="route.name === 'VODBucketPlayerSettings' || route.name ===  'VODBucketSecuritySettings'"
        />
        <TabItem
          type="line"
          label="Advertising"
					:to="'/vods/' + vodFolder?._id + '/advertising'"
          :isActive="route.name === 'StreamsManageVodAdvertising'"
        />
        <TabItem
          type="line"
          label="Paywall"
					:to="'/vods/' + vodFolder?._id + '/paywall'"
          :isActive="route.name === 'VODstreamManagePaywall'"
        />
      </TabGroup>
				<!-- live-only bindings copied from StreamsManage (stream-alive, media-pulse,
				     countdown/abr/dvr events) removed: a VOD folder has no live pulse or sidebar -->
				<router-view v-if="vodFolder" v-slot="{ Component }">
					<component :is="Component" :stream="vodFolder" :is-vod-trial="isVodTrial"
						:trial-subscription="trialSubscription" :vod-sub-expired-days="vodTrialExpiredIn"
						:files-loading="filesLoadingForView"
						@stream-updated="onStreamUpdates" @refresh-stream="fetchStreamDetails"
						@video-update="onVideoFileUpdate" @video-removed="onVideoFileRemoved"
						@video-added="fetchStreamVideoFileList" :aioStorageLimit="aioStorageLimit" />
				</router-view>
				<Spinner v-if="isVideoListLoading" text="Retrieving data..." classes="text-dark-8 my-3"
					spinner-color="var(--c-dark-8)" spinner-size="15px" />

			</div>
			<confirm-modal modalId="delete-vod-stream" message="Are you sure you want to delete this folder?"
				modal-type="danger" :body="deleteMessage" @modal-confirm="onStreamDeleteConfirm" />
		</main>
	</div>
</template>

<style scoped>
.vod-nav-panel,
.vod-content-panel {
	width: 100%;
	flex-basis: 100%;
	min-width: 0;
}

@media (min-width: 1024px) {
	.vod-nav-panel {
		width: var(--vod-nav-width);
		flex-basis: var(--vod-nav-width);
	}

	.vod-content-panel:not(.vod-content-panel--full) {
		width: var(--vod-content-width);
		flex-basis: var(--vod-content-width);
	}
}
</style>
